import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  TextField,
  InputAdornment,
  Chip,
} from "@mui/material";
import moment from "moment";
import { useAuth, useTablePagination } from "hooks";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import { useLazyGetDailySalesDetailQuery } from "store/services/reportApi";
import ExportDailyTransactionExcel from "./ExportDailyTransactionExcel";
import { BaseTabs } from "components/Shared/BaseTabs";
import { Search } from "@mui/icons-material";
import HighlightedText from "components/Shared/HighlightedText";

const DailyTransactionDetail = () => {
  const { date } = useParams();
  const [searchParams] = useSearchParams();
  const { viewEntity, isVendor, vendorId } = useAuth();
  const [errorMessage, setErrorMessage] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchKeyword.length === 0 || searchKeyword.length >= 2) {
        setDebouncedSearch(searchKeyword);
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [searchKeyword]);

  const [
    fetchDailySalesDetail,
    { data: detailRes, isLoading, isFetching, error },
  ] = useLazyGetDailySalesDetailQuery();

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();
  const activeTab = searchParams.get("tab") || "coc";
  const branch = searchParams.get("branch") || "all";
  const user = searchParams.get("user") || "all";

  useEffect(() => {
    if (!moment(date, "YYYY-MM-DD", true).isValid()) {
      setErrorMessage("Invalid date format");
      return;
    }

    fetchDailySalesDetail({
      date,
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
      ...(branch !== "all" && { branch }),
      ...(user !== "all" && { user }),
      ...(isVendor && { vendorId }),
      page: currentPage,
      limit: perPage,
      tab: activeTab,
      ...(debouncedSearch && { search: debouncedSearch }),
    });
  }, [fetchDailySalesDetail, activeTab, date, viewEntity, branch, user, isVendor, vendorId, currentPage, perPage, debouncedSearch]);

  useEffect(() => {
    if (error) {
      console.error("Detail API Error:", error);
      setErrorMessage("Failed to fetch details. Retrying...");
      const retry = setTimeout(() => {
        fetchDailySalesDetail({
          date,
          account: viewEntity?.entity?.account?._id,
          entity: viewEntity?.entity?._id,
          ...(branch !== "all" && { branch }),
          ...(user !== "all" && { user }),
          ...(isVendor && { vendorId }),
          page: currentPage,
          limit: perPage,
        });
      }, 2000);
      return () => clearTimeout(retry);
    }
  }, [
    error,
    fetchDailySalesDetail,
    date,
    viewEntity,
    branch,
    user,
    isVendor,
    vendorId,
    currentPage,
    perPage,
  ]);

  useEffect(() => {
    handlePageChange(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, debouncedSearch]);

  const renderAmount = (amountVal, paymentStatus) => {
    const num = Number(amountVal || 0);
    const isRefunded = paymentStatus === "Refunded" || num < 0;
    const absVal = Math.abs(num).toFixed(2);
    return (
      <Typography
        component="span"
        variant="body2"
        sx={{
          color: isRefunded ? "error.main" : "inherit",
          fontWeight: isRefunded ? 600 : "normal",
        }}
      >
        {isRefunded ? `-Rs. ${absVal}` : `Rs. ${absVal}`}
      </Typography>
    );
  };

  const renderPaymentStatusChip = (status) => {
    if (!status) return "N/A";
    let bgcolor = "#E3F2FD";
    let color = "#1565C0";

    if (status === "Refunded") {
      bgcolor = "#FFEBEE";
      color = "#C62828";
    } else if (status === "Paid") {
      bgcolor = "#E8F5E9";
      color = "#2E7D32";
    } else if (status === "Not Paid") {
      bgcolor = "#FFF3E0";
      color = "#E65100";
    } else if (status === "Collected") {
      bgcolor = "#E0F2F1";
      color = "#00695C";
    }

    return (
      <Chip
        label={status}
        size="small"
        sx={{
          bgcolor,
          color,
          fontWeight: 600,
          fontSize: "0.75rem",
        }}
      />
    );
  };

  const cocColumns = [
    {
      name: "ID",
      selector: (row) => row.cocCollectionId,
      cell: (row) => (
        <Link
          title={row?.cocCollectionId}
          to={`/app/crm/coc-collection/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.cocCollectionId || "N/A"}
        </Link>
      ),
      // width: "150px",
    },
    {
      name: "Shipment ID",
      selector: (row) => row.shipmentStringId,
      cell: (row) => (
        <Link
          title={row?.shipmentStringId}
          to={`/app/crm/shipment/${row?.shipmentId}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.shipmentStringId
            ? <HighlightedText text={row.shipmentStringId} highlight={debouncedSearch} />
            : "N/A"}
        </Link>
      ),
      // width: "150px",
    },
    {
      name: "Amount",
      selector: (row) => row.shipmentAmount,
      cell: (row) => renderAmount(row.shipmentAmount, row.paymentStatus),
      // width: "150px",
    },
    {
      name: "Collected Date",
      selector: (row) => row.collectedDate,
      cell: (row) => moment(row.collectedDate).format("MM-DD-YYYY HH:mm"),
      // width: "200px",
    },
    {
      name: "Branch",
      selector: (row) => row.branchName,
      cell: (row) => row.branchName || "N/A",
      // width: "150px",
    },
    // {
    //   name: "User",
    //   selector: (row) => row.userName,
    //   cell: (row) => row.userName || "N/A",
    //   width: "150px",
    // },
    {
      name: "Payment Status",
      selector: (row) => row.paymentStatus,
      cell: (row) => renderPaymentStatusChip(row.paymentStatus),
    },
    {
      name: "Remarks",
      selector: (row) => row.remarks,
      cell: (row) => row.remarks || "N/A",
      // width: "200px",
    },
  ];

  const deliveryColumns = [
    {
      name: "ID",
      selector: (row) => row.deliveryCollectionId,
      cell: (row) => (
        <Link
          title={row?.deliveryCollectionId}
          to={`/app/crm/delivery-collection/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.deliveryCollectionId || "N/A"}
        </Link>
      ),
      // width: "150px",
    },
    {
      name: "Shipment ID",
      selector: (row) => row.shipmentId,
      cell: (row) => (
        <Link
          title={row?.shipmentId}
          to={`/app/crm/shipment/${row?.shipment}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.shipmentId
            ? <HighlightedText text={row.shipmentId} highlight={debouncedSearch} />
            : "N/A"}
        </Link>
      ),
      // width: "150px",
    },
    {
      name: "Amount",
      selector: (row) => row.shipmentAmount,
      cell: (row) => renderAmount(row.shipmentAmount, row.paymentStatus),
      // width: "150px",
    },
    {
      name: "Collected Date",
      selector: (row) => row.collectedDate,
      cell: (row) => moment(row.collectedDate).format("MM-DD-YYYY HH:mm"),
      // width: "200px",
    },
    // {
    //   name: "User",
    //   selector: (row) => row.userName,
    //   cell: (row) => row.userName || "N/A",
    //   width: "150px",
    // },
    {
      name: "Payment Status",
      selector: (row) => row.paymentStatus,
      cell: (row) => renderPaymentStatusChip(row.paymentStatus),
    },
    {
      name: "Remarks",
      selector: (row) => row.remarks,
      cell: (row) => row.remarks || "N/A",
      // width: "200px",
    },
  ];

  const codColumns = [
    {
      name: "ID",
      selector: (row) => row.codTransferId,
      cell: (row) => (
        <Link
          title={row?.codTransferId}
          to={`/app/crm/cod-transfer/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.codTransferId || "N/A"}
        </Link>
      ),
      // width: "150px",
    },
    {
      name: "Shipment ID",
      selector: (row) => row?.shipment,
      cell: (row) => {
        const shipmentText = Array.isArray(row?.shipment) && row.shipment.length > 0
          ? row.shipment.join(", ")
          : null;
        return shipmentText
          ? <HighlightedText text={shipmentText} highlight={debouncedSearch} />
          : "N/A";
      },
    },
    {
      name: "Vendor",
      selector: (row) => row?.vendor?.name,
      cell: (row) =>
        row?.vendorName ? (
          <Link
            title={row?.vendorName}
            to={`/app/crm/vendor-profile/${row?.vendorId}/detail`}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            {row?.vendorName}
          </Link>
        ) : (
          "N/A"
        ),
      // width: "150px",
    },
    {
      name: "Amount",
      selector: (row) => row.transferAmount,
      cell: (row) => `Rs. ${row.transferAmount?.toFixed(2) || "0.00"}`,
      // width: "150px",
    },
    {
      name: "Transfer Date",
      selector: (row) => row.transferDate,
      cell: (row) => moment(row.transferDate).format("MM-DD-YYYY HH:mm"),
      // width: "200px",
    },
    // {
    //   name: "User",
    //   selector: (row) => row.userName,
    //   cell: (row) => row.userName || "N/A",
    //   width: "150px",
    // },
    {
      name: "Remarks",
      selector: (row) => row.remarks,
      cell: (row) => row.remarks || "N/A",
      // width: "200px",
    },
  ];

  const cocQRColumns = [
    {
      name: "ID",
      selector: (row) => row.cocCollectionId,
      cell: (row) => (
        <Link
          title={row?.cocCollectionId}
          to={`/app/crm/coc-collection/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.cocCollectionId || "N/A"}
        </Link>
      ),
    },
    {
      name: "Shipment ID",
      selector: (row) => row.shipmentStringId,
      cell: (row) => (
        <Link
          title={row?.shipmentStringId}
          to={`/app/crm/shipment/${row?.shipmentId}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.shipmentStringId
            ? <HighlightedText text={row.shipmentStringId} highlight={debouncedSearch} />
            : "N/A"}
        </Link>
      ),
    },
    {
      name: "QR Amount",
      selector: (row) => row.qrAmount,
      cell: (row) => renderAmount(row.qrAmount, row.paymentStatus),
    },
    {
      name: "Payment Date",
      selector: (row) => row.paymentDate,
      cell: (row) => moment(row.paymentDate).format("MM-DD-YYYY HH:mm"),
    },
    {
      name: "Merchant",
      selector: (row) => row.merchantName,
      cell: (row) => row.merchantName || "N/A",
    },
    {
      name: "Branch",
      selector: (row) => row.branchName,
      cell: (row) => row.branchName || "N/A",
    },
    {
      name: "Payment Status",
      selector: (row) => row.paymentStatus,
      cell: (row) => renderPaymentStatusChip(row.paymentStatus),
    },
    {
      name: "Remarks",
      selector: (row) => row.remarks,
      cell: (row) => row.remarks || "N/A",
    },
  ];

  const deliveryQRColumns = [
    {
      name: "ID",
      selector: (row) => row.deliveryCollectionId,
      cell: (row) => (
        <Link
          title={row?.deliveryCollectionId}
          to={`/app/crm/delivery-collection/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.deliveryCollectionId || "N/A"}
        </Link>
      ),
    },
    {
      name: "Shipment ID",
      selector: (row) => row.shipmentId,
      cell: (row) => (
        <Link
          title={row?.shipmentId}
          to={`/app/crm/shipment/${row?.shipment}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.shipmentId
            ? <HighlightedText text={row.shipmentId} highlight={debouncedSearch} />
            : "N/A"}
        </Link>
      ),
    },
    {
      name: "QR Amount",
      selector: (row) => row.qrAmount,
      cell: (row) => renderAmount(row.qrAmount, row.paymentStatus),
    },
    {
      name: "Payment Date",
      selector: (row) => row.paymentDate,
      cell: (row) => moment(row.paymentDate).format("MM-DD-YYYY HH:mm"),
    },
    {
      name: "Merchant",
      selector: (row) => row.merchantName,
      cell: (row) => row.merchantName || "N/A",
    },
    {
      name: "Payment Status",
      selector: (row) => row.paymentStatus,
      cell: (row) => renderPaymentStatusChip(row.paymentStatus),
    },
    {
      name: "Remarks",
      selector: (row) => row.remarks,
      cell: (row) => row.remarks || "N/A",
    },
  ];

  const deductColumns = [
    {
      name: "COD Transfer ID",
      selector: (row) => row.codTransferId,
      cell: (row) => (
        <Link
          title={row?.codTransferId}
          to={`/app/crm/cod-transfer/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.codTransferId || "N/A"}
        </Link>
      ),
    },
    {
      name: "Shipment ID",
      selector: (row) => row.shipmentStringId,
      cell: (row) =>
        row?.shipmentStringId ? (
          <Link
            title={row.shipmentStringId}
            to={`/app/crm/shipment/${row?.shipmentObjectId}/detail`}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            <HighlightedText text={row.shipmentStringId} highlight={debouncedSearch} />
          </Link>
        ) : (
          "N/A"
        ),
    },
    {
      name: "Vendor",
      selector: (row) => row?.vendorName,
      cell: (row) =>
        row?.vendorName ? (
          <Link
            title={row?.vendorName}
            to={`/app/crm/vendor-profile/${row?.vendorId}/detail`}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            {row?.vendorName}
          </Link>
        ) : (
          "N/A"
        ),
    },
    {
      name: "Deduct Charge",
      selector: (row) => row.deductChargeFromCOD,
      cell: (row) => renderAmount(row.deductChargeFromCOD, row.paymentStatus),
    },
    {
      name: "Transfer Date",
      selector: (row) => row.transferDate,
      cell: (row) => moment(row.transferDate).format("MM-DD-YYYY HH:mm"),
    },
    {
      name: "Payment Status",
      selector: (row) => row.paymentStatus,
      cell: (row) => renderPaymentStatusChip(row.paymentStatus),
    },
    {
      name: "Remarks",
      selector: (row) => row.remarks,
      cell: (row) => row.remarks || "N/A",
    },
  ];

  const breadcrumbs = [
    { title: "Reports", path: "/app/crm/reports/list" },
    {
      title: "Daily Transaction Report",
      path: "/app/crm/reports/transaction-report",
    },
    { title: `Details for ${moment(date).format("MM-DD-YYYY")}` },
  ];

  const transactionTabs = [
    { label: "COC Collections", value: "coc" },
    { label: "Delivery Collections", value: "delivery" },
    { label: "COC QR Payments", value: "cocQR" },
    { label: "Delivery QR Payments", value: "deliveryQR" },
    { label: "COD Transfers", value: "cod" },
    { label: "COD Charge Collection", value: "deduct" },
  ];

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs}>
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <BaseTabs
          tabs={transactionTabs}
          activeTab={activeTab}
          actions={<ExportDailyTransactionExcel activeTab={activeTab} perPage={perPage} />}
        />

        <Box sx={{ px: 2, pt: 2 }}>
          <TextField
            size="small"
            placeholder="Search by Shipment ID..."
            value={searchKeyword}
            onChange={(e) => {
              setSearchKeyword(e.target.value);
              handlePageChange(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 280 }}
          />
        </Box>

        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Daily Transaction Details for {moment(date).format("MM-DD-YYYY")}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {isLoading || isFetching ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {activeTab === "coc" && (
                <>
                  <Typography variant="h6" sx={{ mb: 1 }}>COC Collections</Typography>
                  <AppTable
                    columns={cocColumns}
                    data={detailRes?.cocCollections ?? []}
                    progressPending={isLoading || isFetching}
                    paginationServer
                    paginationPerPage={perPage}
                    paginationTotalRows={detailRes?.cocTotalItems ?? 0}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                  />
                </>
              )}

              {activeTab === "delivery" && (
                <>
                  <Typography variant="h6" sx={{ mb: 1 }}>Delivery Collections</Typography>
                  <AppTable
                    columns={deliveryColumns}
                    data={detailRes?.deliveryCollections ?? []}
                    progressPending={isLoading || isFetching}
                    paginationServer
                    paginationPerPage={perPage}
                    paginationTotalRows={detailRes?.deliveryTotalItems ?? 0}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                  />
                </>
              )}

              {activeTab === "cocQR" && (
                <>
                  <Typography variant="h6" sx={{ mb: 1 }}>COC QR Payments</Typography>
                  <AppTable
                    columns={cocQRColumns}
                    data={detailRes?.cocQRPayments ?? []}
                    progressPending={isLoading || isFetching}
                    paginationServer
                    paginationPerPage={perPage}
                    paginationTotalRows={detailRes?.cocQRTotalItems ?? 0}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                  />
                </>
              )}

              {activeTab === "deliveryQR" && (
                <>
                  <Typography variant="h6" sx={{ mb: 1 }}>Delivery QR Payments</Typography>
                  <AppTable
                    columns={deliveryQRColumns}
                    data={detailRes?.deliveryQRPayments ?? []}
                    progressPending={isLoading || isFetching}
                    paginationServer
                    paginationPerPage={perPage}
                    paginationTotalRows={detailRes?.deliveryQRTotalItems ?? 0}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                  />
                </>
              )}

              {activeTab === "cod" && (
                <>
                  <Typography variant="h6" sx={{ mb: 1 }}>COD Transfers</Typography>
                  <AppTable
                    columns={codColumns}
                    data={detailRes?.codTransfers ?? []}
                    progressPending={isLoading || isFetching}
                    paginationServer
                    paginationPerPage={perPage}
                    paginationTotalRows={detailRes?.codTotalItems ?? 0}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                  />
                </>
              )}
              {activeTab === "deduct" && (
                <>
                  <Typography variant="h6" sx={{ mb: 1 }}>COD Charge Collections</Typography>
                  <AppTable
                    columns={deductColumns}
                    data={detailRes?.deductTransfers ?? []}
                    progressPending={isLoading || isFetching}
                    paginationServer
                    paginationPerPage={perPage}
                    paginationTotalRows={detailRes?.deductTotalItems ?? 0}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                  />
                </>
              )}
            </>
          )}
        </Box>

        <Snackbar open={!!errorMessage} autoHideDuration={6000} onClose={() => setErrorMessage("")}>
          <Alert severity="error" onClose={() => setErrorMessage("")}>{errorMessage}</Alert>
        </Snackbar>
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default DailyTransactionDetail;
